import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeOptions } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { FormContainer } from '@strata/react-form-container';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import {
    ProductPayerConfigBulkUploadErrorMessage,
    ProductPayerConfigBulkUploadErrorsDialog,
} from 'pages/productConfig/ProductPayerConfigBulkUploadErrors';
import {
    generateProductPayerConfigTemplate,
    MultiSegmentUploadNotes,
    uploadProductPayerConfigFile,
} from 'pages/productConfig/ProductPayerConfigBulkUploadSupport';
import { ProductPayerConfigBulkUploadTips } from 'pages/productConfig/ProductPayerConfigBulkUploadTips';
import { ProductPayerConfigFileUpload } from 'pages/productConfig/ProductPayerConfigFileUpload';
import { handleError } from 'services/ErrorManagement';
import { useGet } from 'services/Http';
import { mapPayerSegment } from 'services/PayerSegmentSupport';

export const ProductSegmentConfigBulkUpload = () => {
    const payerSegments = useGet('/reference/payer_segments');

    const payerSegmentOptions = payerSegments
        ?.map(mapPayerSegment)
        .sort(({ name: a }, { name: b }) => sortAsc(a, b));

    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);

    return (
        <Formik
            initialValues={{ payerSegments: [], benefit: undefined }}
            onSubmit={async (values, { setFieldValue, resetForm }) => {
                try {
                    const validationErrors = await uploadProductPayerConfigFile(
                        values.payerSegments.map((segment) => segment.value),
                        values.fileToUpload,
                        { benefit: values.benefit },
                    );

                    if (validationErrors) {
                        handleError('File not uploaded. Invalid values found in file.');
                        await setFieldValue('validationErrors', validationErrors);
                    } else {
                        SuccessToast({ message: 'File successfully uploaded.' });
                        resetForm();
                    }
                } catch (err) {
                    handleError('An error occurred uploading file. Please try again.', err);
                }
            }}
            validationSchema={Yup.object({
                payerSegments: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
                benefit: Yup.string().required(validationStrings.requiredField.errorMessage),
                productSegmentConfigFile: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .length(1, validationStrings.requiredField.errorMessage),
            })}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Bulk Upload Product Payer Config"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !formik.isValid || !formik.dirty}
                    isLoading={formik.isSubmitting}
                    formHeaderRightContent={
                        <ActionButtonText
                            label="Generate File"
                            leftIcon={IconNames.IMPORT}
                            onClick={generateProductPayerConfigTemplate}
                        />
                    }
                    formLeftContent={
                        <>
                            <ProductPayerConfigBulkUploadErrorsDialog
                                validationErrors={formik.values.validationErrors}
                                showDialog={showValidationErrorDialog}
                                onCloseDialog={() => setShowValidationErrorDialog(false)}
                            />
                            <SuggestMultiSelectFormik
                                id="payerSegments"
                                label="Payer Segments"
                                required
                                values={payerSegmentOptions}
                                isLoading={!payerSegmentOptions}
                            />
                            <SuggestSingleSelectFormik
                                id="benefit"
                                label="Benefit"
                                required
                                values={BenefitTypeOptions}
                                selectedItem={BenefitTypeOptions.find(
                                    ({ value }) => value === formik.values.benefit,
                                )}
                                onChange={(newValue) =>
                                    formik.setFieldValue('benefit', newValue?.value)
                                }
                            />
                            <ProductPayerConfigFileUpload />
                            <ProductPayerConfigBulkUploadErrorMessage
                                validationErrors={formik.values.validationErrors}
                                onOpenDialog={() => setShowValidationErrorDialog(true)}
                            />
                        </>
                    }
                    formRightContent={
                        <ProductPayerConfigBulkUploadTips extraNotes={MultiSegmentUploadNotes} />
                    }
                />
            )}
        </Formik>
    );
};
